import {
  DayPart,
  PlanningEntityIdentifier,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import styled from 'styled-components';

import { Event } from '../../types';

import { CARD_WIDTH } from 'constants/widths';

import { Box } from '@mui/material';

import {
  ActivityCard,
  ConditionalDraggableCard
} from '../ActivityCard/ActivityCard';
import FreeTime from '../FreeTime/FreeTime';
import TravelTime from '../TravelTime/TravelTime';
import Dropzone from './Dropzone';
import { useCallback, useEffect, useState } from 'react';

const List = styled(Box)<{ height?: string }>`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  height: ${({ height }) => height || '100%'};
  padding: 10px;
  list-style: none;

  .freetime-container:last-child {
    flex-grow: 1;
    padding: 0;
  }

  .dropzone:last-child {
    flex-grow: 1;

    .dropzone__indicator {
      top: 0px;
    }
  }
`;

const Container = styled(Box)`
  margin-top: 8px;
`;

interface InnerListProps {
  activeEntityContainingSelection: string | null;
  dayPart?: DayPart;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier?: PlanningEntityIdentifier;
  scheduleStart: string;
  // Note scheduleId is a frontend id purely used to identify the precise schedule for selection purposes; only used in RosterView
  scheduleId?: string;
  readOnly?: boolean;
  events: Event[];
  transportationMode?: TransportationMode;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
}

const InnerList = ({
  activeEntityContainingSelection,
  dayPart = 'MORNING',
  events,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier,
  scheduleStart,
  scheduleId,
  transportationMode,
  readOnly,
  onChangeActiveEntity,
  onChangeSelection
}: InnerListProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // We look to scheduleId for the RosterView, planningEntityId for the Timeline- and CardsView
  const isActiveEntity: boolean =
    activeEntityContainingSelection === (scheduleId || planningEntityId); // The active route

  const onHandleSelect = useCallback((id: string) => {
    setSelectedIds((selectedIds) => {
      // Call onChangeActiveEntity from parent component so it may update activeEntityContainingSelection
      onChangeActiveEntity?.(scheduleId || planningEntityId);

      if (selectedIds.includes(id))
        return selectedIds.filter((value) => value !== id);
      if (!selectedIds.length) return [id];
      return selectedIds.concat(id);
    });
  }, []);

  // If the active entity (the route) is *not* the entity containing the selected appointments, clear the selection
  useEffect(() => {
    if (!isActiveEntity && selectedIds.length > 0) setSelectedIds([]);
  }, [isActiveEntity]);

  // If the selection changes, notify the parent
  useEffect(() => {
    onChangeSelection?.(selectedIds);
  }, [selectedIds]);

  return (
    <List component="ul">
      {events.map((a, i: number) => {
        const key = `${
          a?.start || a?.scheduleStart || a?.category
        }-${a?.id}-${i}`;
        if (a?.category === 'TRAVELTIME') {
          return (
            <TravelTime
              key={key}
              id={a.id}
              dayPart={dayPart}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              droppable={a.droppable}
              durationInSeconds={a.durationInSeconds}
              scheduleStart={scheduleStart}
              start={a.start}
              transportationMode={transportationMode}
            />
          );
        }

        if (a?.category === 'FREE') {
          return (
            <FreeTime
              key={key}
              id={a.id}
              dayPart={dayPart}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              durationInSeconds={a.durationInSeconds}
              scheduleStart={scheduleStart}
              start={a.start}
            />
          );
        }

        if (a?.category === 'DROPZONE') {
          return (
            <Dropzone
              key={key}
              id={a.id}
              planningEntityType={planningEntityType}
              planningEntityId={planningEntityId}
              planningEntityIdentifier={planningEntityIdentifier!}
              scheduleStart={scheduleStart}
              start={a.start}
            />
          );
        }

        if (planningEntityId === '') {
          return (
            <Container key={key}>
              <ActivityCard
                id={a.id}
                appointmentGrouping={a.appointmentGrouping}
                selected={false}
                planningEntityId={planningEntityId}
                planningEntityType={planningEntityType}
                organizationalUnit={a.organizationalUnit}
                activity={a.activity}
                workPlace={a.workPlace}
                project={a.project}
                employeeType={a.employeeType}
                clients={a.clients}
                employees={a.employees}
                description={a.description}
                durationInSeconds={a.durationInSeconds}
                warnings={a.warnings}
                isAssigned={a.isAssigned}
                isAbsent={a.isAbsent}
                absenceReason={a.absenceReason}
                isEditable={a.isEditable}
                isCreditable={a.isCreditable}
                isCredit={a.isCredit}
                isRecurring={a.isRecurring}
                isOpen={a.isOpen}
                recurrenceRule={a.recurrenceRule}
                start={a.start}
                timeWindow={a.timeWindow}
                location={a.location}
                schedulingLabel={a.schedulingLabel}
              />
            </Container>
          );
        }

        return (
          <ConditionalDraggableCard
            key={key}
            id={a.id}
            appointmentGrouping={a.appointmentGrouping}
            readOnly={readOnly}
            planningEntityType={planningEntityType}
            planningEntityId={planningEntityId}
            onHandleSelect={onHandleSelect}
            scheduleStart={scheduleStart}
            scheduleId={scheduleId}
            organizationalUnit={a.organizationalUnit}
            activity={a.activity}
            workPlace={a.workPlace}
            project={a.project}
            employeeType={a.employeeType}
            clients={a.clients}
            employees={a.employees}
            description={a.description}
            durationInSeconds={a.durationInSeconds}
            warnings={a.warnings}
            isAbsent={a.isAbsent}
            absenceReason={a.absenceReason}
            isAssigned={a.isAssigned}
            isEditable={a.isEditable}
            isCreditable={a.isCreditable}
            isCredit={a.isCredit}
            isRecurring={a.isRecurring}
            isOpen={a.isOpen}
            recurrenceRule={a.recurrenceRule}
            start={a.start}
            timeWindow={a.timeWindow}
            location={a.location}
            schedulingLabel={a.schedulingLabel}
            selected={selectedIds.includes(a.id)}
          />
        );
      })}
    </List>
  );
};

export default InnerList;
